import React from 'react';
import { BookMarked, Brain, ChevronRight, ExternalLink, Globe, Info, MapPin, Scale, ShieldCheck } from 'lucide-react';
import { PortalBadge } from '../arkorbit-ui/PortalBadge';
import {
  collectLexNormIds,
  extractArkoSources,
  extractLexQuotesFromText,
  hasArkoLexSources,
  type ArkoSourceItem,
} from './arkoSources';
import type { MemProofItem } from './memcore';
import {
  hasJudicatureProofData,
  stripJudicatureStampForDisplay,
  type JudicatureProof,
  type JudicatureProofCase,
} from './judicatureProof';
import {
  explainLexProof,
  formatBotMessageForDisplay as formatLexBubbleText,
  formatLexRetrievedAt,
  hasLexProofData,
  hasLexQuoteBlock,
  type LexProof,
  type LexProofSource,
} from './lexProof';
import { classifyArkoTrust } from './arkoTrust';
import { JOIN_SYNTHESIS_CITE_MAX } from './portalLexJudJoin';
import { quelleGueltigAbLine } from './portalParagraphAnschluss';
import {
  hasWebSearchProofData,
  plainWebDisplayText,
  stripWebStampForDisplay,
  webUrlLabel,
  type WebSearchProof,
  type WebSearchProofSource,
} from './webSearchProof';

export { classifyArkoTrust, type ArkoTrust, type ArkoTrustLevel } from './arkoTrust';

const RIS_TITLE = 'Quelle im Rechtsinformationssystem des Bundes (RIS) öffnen';

/**
 * Sichtbares Vertrauens-Badge über/neben der Antwort:
 * grün „verifiziert · [Norm] · RIS" vs. grau „allgemeine Orientierung".
 */
export const ArkoTrustBadge: React.FC<{
  text: string;
  lexProof?: LexProof | null;
  verifiedTitle?: string;
  unverifiedTitle?: string;
  lawyerFacing?: boolean;
  akteLook?: boolean;
}> = ({ text, lexProof, verifiedTitle, unverifiedTitle, lawyerFacing, akteLook = false }) => {
  const trust = classifyArkoTrust(text, lexProof);
  const verifiedTip =
    verifiedTitle ||
    (lawyerFacing
      ? 'Wortlaut aus dem Normenbestand — Quellen im Nachweis.'
      : 'Verifizierter Wortlaut aus dem LEXCORE-Snapshot — Quellen im Nachweis unten.');
  const unverifiedTip =
    unverifiedTitle ||
    (lawyerFacing
      ? 'Allgemeine Orientierung ohne belegte Quelle — keine Rechtsberatung.'
      : 'Allgemeine Orientierung ohne verifizierte LEXCORE-Quelle — keine verbindliche Rechtsauskunft.');

  if (trust.level === 'verified') {
    const normLabel =
      trust.normIds.length === 1
        ? trust.normIds[0]
        : trust.normIds.length > 1
          ? `${trust.normIds.length} Normen`
          : null;
    return (
      <span
        className="portal-arko-beleg inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold"
        title={verifiedTip}
      >
        <ShieldCheck className="w-3 h-3" aria-hidden />
        {lawyerFacing ? 'belegt' : 'verifiziert'}
        {normLabel ? (
          <span className="font-mono">· {normLabel}</span>
        ) : null}
        {trust.risUrl ? (
          <a
            href={trust.risUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-0.5 underline underline-offset-2"
            title={RIS_TITLE}
          >
            <ExternalLink className="w-3 h-3" aria-hidden />
            RIS
          </a>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className={
        akteLook
          ? 'inline-flex items-center gap-1.5 rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-0.5 text-[10px] font-medium text-violet-100'
          : 'inline-flex items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium text-slate-400'
      }
      title={unverifiedTip}
    >
      <Info className="w-3 h-3" aria-hidden />
      allgemeine Orientierung
    </span>
  );
};

function renderLexSourceItem(item: ArkoSourceItem, index: number) {
  if (item.kind === 'lex_stamp') {
    return (
      <PortalBadge
        key={`lex-${index}`}
        label={item.raw}
        accent="emerald"
        className="!px-2 !py-0.5 !text-[9px] !font-mono max-w-full truncate"
      />
    );
  }
  if (item.kind === 'norm') {
    return (
      <PortalBadge
        key={`norm-${item.normId}-${index}`}
        label={item.normId}
        accent="violet"
        className="!px-2 !py-0.5 !text-[9px] !font-mono"
      />
    );
  }
  if (item.kind === 'ris') {
    return (
      <a
        key={`ris-${index}`}
        href={item.url.startsWith('http') ? item.url : undefined}
        target="_blank"
        rel="noreferrer"
        className="portal-para-quelle block rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 text-sm font-semibold text-slate-100 underline underline-offset-2"
        title={quelleGueltigAbLine(item.validFrom) || undefined}
      >
        RIS
        {quelleGueltigAbLine(item.validFrom) ? ` · ${quelleGueltigAbLine(item.validFrom)}` : ''}
      </a>
    );
  }
  return null;
}

function LexProofSourceCard({ source }: { source: LexProofSource }) {
  const risHref = source.ris_url?.startsWith('http') ? source.ris_url : undefined;
  const gueltigAb = quelleGueltigAbLine(source.valid_from);
  return (
    <div className="portal-arko-wortlaut-karte rounded border border-white/[0.08] px-2 py-1.5 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <PortalBadge
          label={source.norm_id}
          accent="violet"
          className="!px-2 !py-0.5 !text-[9px] !font-mono"
        />
      </div>
      {source.title ? (
        <p className="text-[10px] font-medium text-slate-300 leading-snug">{source.title}</p>
      ) : null}
      {source.fragment_text ? (
        <p className="text-[10px] text-slate-400 leading-snug whitespace-pre-wrap border-l border-white/[0.06] pl-2">
          {source.fragment_text}
        </p>
      ) : null}
      {gueltigAb || risHref ? (
        <div
          className="portal-para-quelle rounded-md border border-violet-400/30 bg-[#07070a] px-2 py-1.5 text-sm font-semibold text-slate-100"
          data-testid="portal-para-quelle"
        >
          {gueltigAb ? <p data-testid="portal-para-gueltig">{gueltigAb}</p> : null}
          {risHref ? (
            <a
              href={risHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 underline underline-offset-2"
              title={RIS_TITLE}
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
              RIS
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Eine Kontaktkarte. Keine zweite Trefferliste daneben. */
export function webContactCard(webProof?: WebSearchProof | null): boolean {
  return Boolean(webProof?.contact?.source_url?.startsWith('https://'));
}

export const WebContactCard: React.FC<{ webProof?: WebSearchProof | null }> = ({ webProof }) => {
  const contact = webProof?.contact;
  if (!contact?.source_url?.startsWith('https://')) return null;
  const name = plainWebDisplayText(contact.name || '');
  const address = plainWebDisplayText(contact.address || '');
  const phone = plainWebDisplayText(contact.phone || '');
  const email = plainWebDisplayText(contact.email || '');
  const title = plainWebDisplayText(contact.source_title || name);
  const heading = !contact.fallback && name && address
    ? (/^kanzlei\b/i.test(name) ? `${name} befindet sich hier:` : `Die Kanzlei ${name} befindet sich hier:`)
    : '';
  return (
    <div data-testid="arko-web-contact-card" className="portal-web-contact whitespace-normal space-y-2">
      {heading ? <p className="m-0 text-sm font-medium">{heading}</p> : null}
      {contact.fallback ? (
        <p className="m-0 text-sm font-medium">{title || 'Quelle'}</p>
      ) : null}
      {!contact.fallback && address ? (
        <p className="m-0 flex flex-wrap items-center gap-2 text-sm">
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{address}</span>
          {phone || email ? <span className="portal-web-contact-mark">Kontakt</span> : null}
        </p>
      ) : null}
      {!contact.fallback && phone ? <p className="m-0 text-sm">Telefon: {phone}</p> : null}
      {!contact.fallback && email ? <p className="m-0 text-sm">E-Mail: {email}</p> : null}
      {!contact.fallback && contact.website ? (
        <p className="m-0 text-sm">
          Website:{' '}
          <a href={contact.website} target="_blank" rel="noreferrer">
            {webUrlLabel(contact.website).replace(/^https:\/\//, '')}
          </a>
        </p>
      ) : null}
      {!contact.fallback && contact.maps_url && address ? (
        <p className="m-0 flex items-center gap-2 text-sm">
          <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <a href={contact.maps_url} target="_blank" rel="noreferrer">Adresse in Google Maps öffnen</a>
        </p>
      ) : null}
      <p className="m-0 text-sm">
        <a href={contact.source_url} target="_blank" rel="noreferrer">{webUrlLabel(contact.source_url)}</a>
      </p>
      <p className="m-0 text-[13px] opacity-80">Fakten nur aus dieser Quelle.</p>
    </div>
  );
};

/** Eine Liste in der Blase. Die Klappe wiederholt dieselben Treffer nicht. */
export function webSourcesLead(webProof?: WebSearchProof | null): boolean {
  return Boolean(webProof && webProof.status === 'ok' && webProof.sources.length > 0);
}

export const WebRechercheLead: React.FC<{ webProof?: WebSearchProof | null }> = ({ webProof }) => {
  if (!webSourcesLead(webProof) || !webProof) return null;
  return (
    <div data-testid="arko-web-recherche-lead" className="whitespace-normal space-y-3">
      <p className="m-0 text-sm text-slate-300">Fakten nur aus diesen Quellen.</p>
      <ol className="m-0 list-none space-y-4 p-0">
        {webProof.sources.map((s: WebSearchProofSource, i) => (
          <li key={s.url} className="space-y-1">
            <p className="m-0 text-sm font-medium text-slate-100">
              {i + 1}. {plainWebDisplayText(s.title)}
            </p>
            {s.snippet ? (
              <p className="m-0 text-sm leading-relaxed text-slate-300">{plainWebDisplayText(s.snippet)}</p>
            ) : null}
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer"
              className="block break-all text-sm text-sky-300 hover:text-sky-200"
            >
              {webUrlLabel(s.url)}
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
};

/** Recherche-Klappe — Pattern LEX/OGH-Original, URLs Pflicht. */
export const WebRechercheFold: React.FC<{ webProof?: WebSearchProof | null }> = ({ webProof }) => {
  const proof = hasWebSearchProofData(webProof) ? webProof! : null;
  if (!proof) return null;
  return (
    <details
      className="mt-2 rounded border border-sky-500/20 bg-sky-500/[0.04] group"
      data-testid="arko-web-recherche-fold"
    >
      <summary className="list-none cursor-pointer select-none flex items-center gap-1.5 px-2 py-1.5">
        <ChevronRight
          className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90 shrink-0"
          aria-hidden
        />
        <Globe className="w-3 h-3 text-sky-300/85 shrink-0" aria-hidden />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-200/90">
          Recherche
        </span>
      </summary>
      <div className="px-2 pb-2 space-y-1.5">
        <p className="text-[9px] font-mono text-sky-300/80">{proof.web_stamp}</p>
        {proof.status !== 'ok' || proof.sources.length === 0 ? (
          <p className="rounded border border-white/[0.08] bg-white/[0.03] px-2 py-1.5 text-[10px] text-slate-400">
            {proof.status === 'stub_no_key'
              ? 'Such-Gate ohne Key — keine Treffer, nichts erfunden.'
              : proof.status === 'rejected_query'
                ? 'Anfrage nicht an die Such-API (Akte oder interne URL).'
                : 'Keine belastbare öffentliche Web-Quelle.'}
          </p>
        ) : (
          proof.sources.map((s: WebSearchProofSource) => (
            <div
              key={s.url}
              className="rounded border border-sky-500/15 bg-sky-500/[0.04] px-2 py-1.5 space-y-1"
            >
              <p className="text-[10px] font-medium text-slate-300 leading-snug">{s.title}</p>
              {s.snippet ? (
                <p className="text-[10px] text-slate-400 leading-snug">{s.snippet}</p>
              ) : null}
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[9px] text-sky-300/85 hover:text-sky-200 break-all"
              >
                <ExternalLink className="w-3 h-3 shrink-0" aria-hidden />
                {webUrlLabel(s.url)}
              </a>
            </div>
          ))
        )}
      </div>
    </details>
  );
};

/** Fall-Join: Originale in zwei Klappen, nicht als Lead. */
export const LexJudJoinFolds: React.FC<{
  lexProof?: LexProof | null;
  judProof?: JudicatureProof | null;
  /** Chip „Was sagt der OGH dazu?“ öffnet die Judikatur, der Kurztext bleibt. */
  judOpenNonce?: number;
}> = ({ lexProof, judProof, judOpenNonce = 0 }) => {
  const structuredJud = hasJudicatureProofData(judProof) ? judProof! : null;
  const hasLex = hasLexQuoteBlock(lexProof);
  const [judOpen, setJudOpen] = React.useState(false);
  React.useEffect(() => {
    if (judOpenNonce > 0) setJudOpen(true);
  }, [judOpenNonce]);
  if (!structuredJud && !hasLex) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid="lex-jud-join-folds">
      {structuredJud ? (
        <details
          className="rounded border border-violet-500/20 bg-violet-500/[0.04] group"
          data-testid="lex-jud-join-fold-jud"
          open={judOpen}
          onToggle={(event) => {
            const next = event.currentTarget.open;
            setJudOpen((prev) => (prev === next ? prev : next));
          }}
        >
          <summary className="list-none cursor-pointer select-none flex items-center gap-1.5 px-2 py-1.5">
            <ChevronRight
              className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90 shrink-0"
              aria-hidden
            />
            <Scale className="w-3 h-3 text-violet-300/85 shrink-0" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-200/90">
              OGH (Original)
            </span>
          </summary>
          <div className="px-2 pb-2">
            <JudicatureStructuredProof proof={structuredJud} citeMax={JOIN_SYNTHESIS_CITE_MAX} />
          </div>
        </details>
      ) : null}
      {hasLex ? (
        <details
          className="rounded border border-emerald-500/20 bg-emerald-500/[0.04] group"
          data-testid="lex-jud-join-fold-lex"
        >
          <summary className="list-none cursor-pointer select-none flex items-center gap-1.5 px-2 py-1.5">
            <ChevronRight
              className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90 shrink-0"
              aria-hidden
            />
            <BookMarked className="w-3 h-3 text-emerald-300/85 shrink-0" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-200/90">
              Normwortlaut
            </span>
          </summary>
          <div className="px-2 pb-2">
            <LexVerifiedQuoteBlock lexProof={lexProof} />
          </div>
        </details>
      ) : null}
    </div>
  );
};

/** Ausführliche Treffer, wenn die Antwort keine Join-Klappe hat. Kurztext bleibt darüber. */
export const OghHitDetail: React.FC<{
  judProof?: JudicatureProof | null;
  open?: boolean;
}> = ({ judProof, open }) => {
  if (!open) return null;
  const structuredJud = hasJudicatureProofData(judProof) ? judProof! : null;
  if (!structuredJud || structuredJud.status !== 'ok' || structuredJud.cases.length === 0) return null;
  return (
    <div
      className="mt-2 rounded border border-violet-500/20 bg-violet-500/[0.04] px-2 py-2"
      data-testid="portal-ogh-detail"
    >
      <JudicatureStructuredProof proof={structuredJud} lawyerFacing citeMax={JOIN_SYNTHESIS_CITE_MAX} />
    </div>
  );
};

/** Sofort sichtbarer LEX-Block (Sources, nicht Modell-Text). */
export const LexVerifiedQuoteBlock: React.FC<{
  lexProof?: LexProof | null;
  heading?: string;
}> = ({ lexProof, heading = 'Verifizierter Wortlaut' }) => {
  if (!hasLexQuoteBlock(lexProof)) return null;
  const sources = (lexProof?.sources ?? []).filter((s) => s.norm_id && s.fragment_text);
  return (
    <div className="mb-2 space-y-1.5" data-testid="lex-quote-block">
      <div className="portal-arko-wortlaut-kopf text-[9px] font-semibold uppercase tracking-wider text-slate-400">
        {heading}
      </div>
      {sources.map((source) => (
        <LexProofSourceCard key={source.norm_id} source={source} />
      ))}
    </div>
  );
};

function LexStructuredProof({
  proof,
  lawyerFacing,
}: {
  proof: LexProof;
  lawyerFacing?: boolean;
}) {
  const when = formatLexRetrievedAt(proof.retrieved_at);
  const sourceCount = proof.sources?.length ?? 0;

  return (
    <div className="space-y-2">
      <p className="text-[10px] text-slate-300 leading-snug">{explainLexProof(proof)}</p>

      <dl className="grid gap-1 text-[9px] text-slate-500">
        {when ? (
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Abgerufen</dt>
            <dd className="text-slate-400">{when}</dd>
          </div>
        ) : null}
        {lawyerFacing ? null : (
          <>
            {proof.snapshot_version ? (
              <div className="flex gap-2">
                <dt className="shrink-0 text-slate-600">Snapshot</dt>
                <dd className="font-mono text-slate-400">{proof.snapshot_version}</dd>
              </div>
            ) : null}
            {proof.audit_id ? (
              <div className="flex gap-2">
                <dt className="shrink-0 text-slate-600">Audit-ID</dt>
                <dd className="font-mono text-slate-400 break-all">{proof.audit_id}</dd>
              </div>
            ) : null}
            {proof.lex_stamp ? (
              <div className="flex gap-2">
                <dt className="shrink-0 text-slate-600">Stempel</dt>
                <dd className="font-mono text-emerald-300/80 break-all">{proof.lex_stamp}</dd>
              </div>
            ) : null}
            {proof.lex_query ? (
              <div className="flex gap-2">
                <dt className="shrink-0 text-slate-600">Suchanfrage</dt>
                <dd className="text-slate-400 whitespace-pre-wrap">{proof.lex_query}</dd>
              </div>
            ) : null}
          </>
        )}
      </dl>

      {sourceCount > 0 ? (
        <div className="space-y-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300/80">
            Quellen ({sourceCount})
          </div>
          {proof.sources!.map((s) => (
            <LexProofSourceCard key={s.norm_id} source={s} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function JudicatureCaseCard({ item }: { item: JudicatureProofCase }) {
  const coreSentences = item.kernsaetze?.length ? item.kernsaetze : item.rechtssaetze;
  return (
    <div className="rounded border border-violet-500/20 bg-violet-500/[0.05] px-2 py-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <PortalBadge
          label={item.geschaeftszahl}
          accent="violet"
          className="!px-2 !py-0.5 !text-[9px] !font-mono"
        />
        <span className="text-[9px] font-mono text-violet-200/70">{item.case_id}</span>
        {item.date ? <span className="text-[9px] text-slate-500">· {item.date}</span> : null}
      </div>
      {item.leitsatz ? (
        <p className="text-[10px] font-medium text-slate-300 leading-snug">{item.leitsatz}</p>
      ) : null}
      {coreSentences?.length ? (
        <ul className="space-y-1 border-l border-violet-400/20 pl-2">
          {coreSentences.map((sentence, index) => (
            <li key={`${item.case_id}-core-${index}`} className="text-[10px] text-slate-400 leading-snug">
              „{sentence}“
            </li>
          ))}
        </ul>
      ) : null}
      {item.norm_refs?.length ? (
        <p className="text-[9px] text-slate-500">
          Normbezüge (nur Verweise): {item.norm_refs.join(', ')}
        </p>
      ) : null}
      <a
        href={item.source_url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-[9px] text-violet-300/85 hover:text-violet-200"
      >
        <ExternalLink className="w-3 h-3" aria-hidden />
        OGH-Quelle im RIS öffnen
      </a>
    </div>
  );
}

function JudicatureStructuredProof({
  proof,
  citeMax,
  lawyerFacing,
}: {
  proof: JudicatureProof;
  citeMax?: number;
  lawyerFacing?: boolean;
}) {
  const all = proof.cases;
  const cap = citeMax && citeMax > 0 ? citeMax : all.length;
  const cite = all.slice(0, cap);
  const more = all.slice(cap);
  return (
    <div className="space-y-2">
      {lawyerFacing ? null : (
        <dl className="grid gap-1 text-[9px] text-slate-500">
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Stempel</dt>
            <dd className="font-mono text-violet-300/85 break-all">{proof.jud_stamp}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="shrink-0 text-slate-600">Audit-ID</dt>
            <dd className="font-mono text-slate-400 break-all">{proof.audit_id}</dd>
          </div>
          {proof.snapshot_version ? (
            <div className="flex gap-2">
              <dt className="shrink-0 text-slate-600">Snapshot</dt>
              <dd className="font-mono text-slate-400">{proof.snapshot_version}</dd>
            </div>
          ) : null}
        </dl>
      )}
      {proof.status === 'no_match' || proof.cases.length === 0 ? (
        <p className="rounded border border-white/[0.08] bg-white/[0.03] px-2 py-1.5 text-[10px] text-slate-400">
          {lawyerFacing
            ? 'Kein passender OGH-Treffer im aktuellen Bestand.'
            : 'Kein passender OGH-Treffer im aktuellen Bestand.'}
        </p>
      ) : (
        <div className="space-y-1.5">
          {cite.map((item) => (
            <JudicatureCaseCard key={item.case_id} item={item} />
          ))}
          {more.length > 0 ? (
            <details
              className="rounded border border-violet-500/15 bg-violet-500/[0.03] group/more"
              data-testid="lex-jud-join-fold-more-hits"
            >
              <summary className="list-none cursor-pointer select-none flex items-center gap-1.5 px-2 py-1.5">
                <ChevronRight
                  className="w-3 h-3 text-slate-500 transition-transform group-open/more:rotate-90 shrink-0"
                  aria-hidden
                />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-200/80">
                  Weitere Treffer ({more.length})
                </span>
              </summary>
              <div className="px-2 pb-2 space-y-1.5">
                {more.map((item) => (
                  <JudicatureCaseCard key={item.case_id} item={item} />
                ))}
              </div>
            </details>
          ) : null}
        </div>
      )}
    </div>
  );
}

function formatMemDate(iso: string): string {
  if (!iso) return '';
  const d = iso.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  return iso;
}

interface ArkoResponseProofProps {
  text: string;
  /** Modul C: aus Recall — nicht im Fließtext (§7.5). */
  memProof?: MemProofItem[];
  /** Strukturierter LEXCORE-Nachweis aus n8n (bevorzugt gegenüber Text-Parsing). */
  lexProof?: LexProof | null;
  /** Strukturierter OGH-Nachweis; wird immer in einem eigenen JUD-Block gerendert. */
  judProof?: JudicatureProof | null;
  /** Internet-Recherche (ARKORBIT-Gate), Klappe mit URLs. */
  webProof?: WebSearchProof | null;
  className?: string;
  /** Suche-Demo: Label ohne Intern-Kürzel LEX/JUD. */
  summaryLabel?: string;
  lexSectionLabel?: string;
  lawyerFacing?: boolean;
  /** Suche im Akten-Look: Würfel statt Chevron, keine weiße Pille, keine blaue Leiste. */
  akteLook?: boolean;
}

/** Ausklappbar: MEM / LEX / MEM+LEX — Quellen, Warum, Wann (SKALIERUNG §7.5). */
export const ArkoResponseProof: React.FC<ArkoResponseProofProps> = ({
  text,
  memProof,
  lexProof,
  judProof,
  webProof,
  className,
  summaryLabel,
  lexSectionLabel,
  lawyerFacing,
  akteLook = false,
}) => {
  const structuredProof = hasLexProofData(lexProof) ? lexProof! : null;
  const textQuotes = extractLexQuotesFromText(text);
  const mergedProof: LexProof | null = structuredProof
    ? {
        ...structuredProof,
        sources:
          structuredProof.sources && structuredProof.sources.length > 0
            ? structuredProof.sources
            : textQuotes,
      }
    : textQuotes.length > 0
      ? { sources: textQuotes }
      : null;
  const hasStructuredLex = hasLexProofData(mergedProof);
  const lexSources = hasStructuredLex
    ? []
    : extractArkoSources(text).filter(
        (item) => item.kind === 'lex_stamp' || item.kind === 'norm' || item.kind === 'ris',
      );
  const hasMem = Boolean(memProof && memProof.length > 0);
  const hasLex = hasStructuredLex || lexSources.length > 0;
  const structuredJud = hasJudicatureProofData(judProof) ? judProof! : null;
  const hasJud = structuredJud !== null;
  const structuredWeb = hasWebSearchProofData(webProof) ? webProof! : null;
  const hasWeb = structuredWeb !== null;
  if (!hasMem && !hasLex && !hasJud && !hasWeb) return null;

  const labelParts: string[] = [];
  if (hasMem) labelParts.push('MEM');
  if (hasLex) labelParts.push('LEX');
  if (hasJud) labelParts.push('JUD');
  if (hasWeb) labelParts.push('WEB');

  const lexSummary = hasStructuredLex && mergedProof?.sources?.length
    ? ` · ${mergedProof.sources.length} Quelle${mergedProof.sources.length === 1 ? '' : 'n'}`
    : '';
  const proofTitle = lawyerFacing
    ? `${summaryLabel || 'Nachweis'}${lexSummary}`
    : `Nachweis (${labelParts.join('+')})${lexSummary}`;

  return (
    <details className={className ?? 'mt-1.5 max-w-[80%] group'}>
      <summary className="list-none cursor-pointer select-none flex items-center gap-1">
        {akteLook ? (
          <span className="relative block h-7 w-7 shrink-0 [perspective:280px]" aria-hidden>
            <span className="relative block h-full w-full transition-transform duration-500 [transform-style:preserve-3d] group-open:rotate-x-180">
              <span className="portal-suche-nachweis-face absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md border border-violet-400/40 bg-violet-600/30 text-violet-200 [backface-visibility:hidden]">
                <BookMarked className="h-3.5 w-3.5" aria-hidden />
              </span>
              <span
                className="absolute inset-0 inline-flex h-full w-full items-center justify-center rounded-md bg-violet-600 text-white [backface-visibility:hidden]"
                style={{ transform: 'rotateX(180deg)' }}
              >
                <BookMarked className="h-3.5 w-3.5" aria-hidden />
              </span>
            </span>
          </span>
        ) : (
          <ChevronRight
            className="w-3 h-3 text-slate-500 transition-transform group-open:rotate-90"
            aria-hidden
          />
        )}
        <span
          className={
            akteLook
              ? 'portal-suche-nachweis-chip text-[11px] font-semibold text-violet-100'
              : 'inline-flex items-center gap-1 rounded-full border border-white/[0.08] bg-white/[0.03] px-2 py-0.5 text-[10px] font-semibold text-slate-400 hover:text-slate-200 hover:border-emerald-500/30 transition-colors'
          }
        >
          {akteLook ? null : <BookMarked className="w-3 h-3" aria-hidden />}
          {proofTitle}
        </span>
      </summary>
      <div className={`mt-2 pl-1 space-y-2 ml-1 ${akteLook ? 'border-l border-violet-400/30' : 'border-l border-white/[0.08]'}`}>
        {hasMem ? (
          <div className="space-y-1.5">
            <div className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider ${akteLook ? 'text-violet-200/80' : 'text-blue-300/80'}`}>
              <Brain className="w-3 h-3" aria-hidden />
              Gedächtnis
            </div>
            {memProof!.map((item) => (
              <div
                key={item.vermerk_id}
                className={
                  akteLook
                    ? 'rounded border border-violet-400/30 bg-[#07070a] px-2 py-1.5'
                    : 'rounded border border-blue-500/15 bg-blue-500/[0.04] px-2 py-1.5'
                }
              >
                <div className={`text-[9px] font-mono ${akteLook ? 'text-violet-200/80' : 'text-blue-200/90'}`}>
                  {item.vermerk_id}
                  {item.gueltig_von ? ` · ${formatMemDate(item.gueltig_von)}` : ''}
                  {item.vertrauen ? ` · ${item.vertrauen}` : ''}
                </div>
                <p className="mt-0.5 text-[10px] text-slate-300 leading-snug whitespace-pre-wrap">
                  {item.inhalt}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {hasLex ? (
          <div className="space-y-1.5">
            <div className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300/80">
              {lawyerFacing ? lexSectionLabel || 'Quellen' : 'LEXCORE'}
            </div>
            {hasStructuredLex && mergedProof ? (
              <LexStructuredProof proof={mergedProof} lawyerFacing={lawyerFacing} />
            ) : (
              lexSources.map((item, i) => renderLexSourceItem(item, i))
            )}
          </div>
        ) : null}
        {hasJud && structuredJud ? (
          <div className={`space-y-1.5 border-t pt-2 ${akteLook ? 'border-violet-400/30' : 'border-white/[0.08]'}`}>
            <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-violet-300/85">
              <Scale className="w-3 h-3" aria-hidden />
              OGH-Judikatur
            </div>
            <JudicatureStructuredProof proof={structuredJud} lawyerFacing={lawyerFacing} />
          </div>
        ) : null}
        {hasWeb && structuredWeb ? (
          <div className={`space-y-1.5 border-t pt-2 ${akteLook ? 'border-violet-400/30' : 'border-white/[0.08]'}`}>
            <div className={`flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider ${akteLook ? 'text-violet-200/80' : 'text-sky-300/85'}`}>
              <Globe className="w-3 h-3" aria-hidden />
              Recherche
            </div>
            {structuredWeb.sources.length === 0 ? (
              <p className="text-[10px] text-slate-400">Keine öffentliche URL.</p>
            ) : (
              structuredWeb.sources.map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className={
                    akteLook
                      ? 'block truncate text-[10px] text-violet-200/80 hover:text-violet-100'
                      : 'block text-[10px] text-sky-300/85 hover:text-sky-200 truncate'
                  }
                >
                  {s.title}
                </a>
              ))
            )}
          </div>
        ) : null}
      </div>
    </details>
  );
};

/** @deprecated Alias — nutze ArkoResponseProof */
export const ArkoBotSources: React.FC<ArkoResponseProofProps> = (props) => (
  <ArkoResponseProof {...props} />
);

/** Bot-Text: bei LEX nur Kurzzeile — Details im Ausklapp-Block. */
export function formatBotMessageForDisplay(
  text: string,
  lexProof?: LexProof | null,
  judProof?: JudicatureProof | null,
  webProof?: WebSearchProof | null,
): string {
  const normIds = collectLexNormIds(text, lexProof);
  const lexFormatted = formatLexBubbleText(text, lexProof, normIds);
  const afterJud = hasJudicatureProofData(judProof) || /\[JUD:/i.test(lexFormatted)
    ? stripJudicatureStampForDisplay(lexFormatted)
    : lexFormatted;
  const stripped = hasWebSearchProofData(webProof) || /\[WEB:/i.test(afterJud)
    ? stripWebStampForDisplay(afterJud)
    : afterJud;
  const sourced = Boolean(
    webProof
    && (
      (webProof.contact && webProof.contact.source_url)
      || (Array.isArray(webProof.sources) && webProof.sources.length > 0)
    ),
  );
  if (sourced) return stripped;
  return stripped
    .replace(/[ \t]*Fakten nur aus dieser Quelle\.?[ \t]*/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Legacy-Helfer: LEX/MEM-Nachweis vorhanden? */
export function hasArkoProof(
  text: string,
  memProof?: MemProofItem[],
  lexProof?: LexProof | null,
  judProof?: JudicatureProof | null,
  webProof?: WebSearchProof | null,
): boolean {
  return (
    Boolean(memProof && memProof.length > 0) ||
    hasLexProofData(lexProof) ||
    hasJudicatureProofData(judProof) ||
    hasWebSearchProofData(webProof) ||
    hasArkoLexSources(text)
  );
}
